import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Popper, { PopperPlacementType } from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./TrendsFilter.css";
import {
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { FixedSizeList } from "react-window";
import {
  FILTER_CONFIGS,
  FilterConfig,
} from "../helpers/funcs/filterConfigTrends";
import { useDispatch } from "react-redux";
import {
  clearAllTimeUnits,
  clearAllTimeUnitsData,
  clearTrendsFilters,
  setTrendsFilter,
  removeTrendsFilter,
  setLoadingTrafficSourceFilters,
} from "../../../../../features/trendsSlice";
import { useTrendsLogic } from "../helpers/hooks/useGetTrendsAPI";
import { selectUserToken } from "../../../../../features/userSlice";
import { TrendsDashboardEndPointLogic } from "../../../../../logic/AssetManagement/Trends/TrendsDashboardEndPointLogic";
import { useSearchParams } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  TrendsDataSetQueryIds,
  TrendsFilterQueryIds,
} from "../helpers/funcs/trendsAllQueryIDs";
import CloseIcon from "@mui/icons-material/Close";
// Define filter configuration interface
interface TrendsQueryParams {
  oauthTokenHeader: any;
  queryId: any;
  startDate: any;
  endDate: any;
  timeUnit: any;
  websiteSharedUids?: any[];
  dimensions?: any[];
  type: any;
  campaignId?: any;
  adgroupId?: any;
  matchtype?: any;
  kwId?: any;
  country?: any;
  trafficSource?: any;
}
interface CampaignFilterParams {
  queryId: any;
  oauthTokenHeader: any;
  type: any;
  key: any;
  websiteSharedUids: any[];
  dimensions: any[];
  trafficSource: any;
  account_shared_uid?: number;
  campaign_status?: string;
}
export default function TrendsFilter() {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] =
    useState<PopperPlacementType>("bottom-start");
  const [selectedContentIndex, setSelectedContentIndex] = useState<number>(0);
  const oauthTokenHeader: any = useSelector(selectUserToken);
  // Dynamic state management
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>(
    {},
  );
  const [clicked, setClicked] = useState<boolean[]>(
    new Array(FILTER_CONFIGS.length).fill(false),
  );
  const { userData } = useSelector((state: any) => state.user);
  const isTrafficSourceSelected = !!selectedValues["trafficsource"];
  const isFilterDisabled = (config: FilterConfig) => {
    if (config.key === "trafficsource") {
      return false; // Traffic source is always enabled
    }
    return loadingTrafficSourceFilters || !isTrafficSourceSelected; // Other filters disabled until traffic source is selected
  };
  const {
    filterBizLayerQueryDataMap,
    trafficSourceData,
    selectedStartDate,
    selectedEndDate,
    geoData,
    trendsFilters,
    selectedSharedUid,
    fetchBy,
    dataSet,
    organizationDimensions,
    loadingTrafficSourceFilters,
    defaultWebsiteUid,
    defaultDimension,
  } = useSelector((state: any) => state.trends);
  const [searchParams, setSearchParams] = useSearchParams(); // Get URL params
  const [isInitialized, setIsInitialized] = useState(false);
  const { fetchFilteredTrendsData } = useTrendsLogic();
  const getFilterParamKey = (key: string): string => {
    switch (key.toLowerCase()) {
      case "kwid":
        return "kwId";
      case "trafficsource":
        return "trafficSource";
      default:
        return key;
    }
  };

  useEffect(() => {
    if (!isInitialized && userData?.data) {
      const initialFilters: Record<string, string> = {};

      // Extract filter values from URL params
      FILTER_CONFIGS.forEach((config) => {
        const paramKey = getFilterParamKey(config.key);
        const paramValue = searchParams.get(paramKey);
        if (paramValue && paramValue !== "all" && paramValue !== "1") {
          initialFilters[config.key] = paramValue;
          dispatch(setTrendsFilter({ key: config.key, value: paramValue }));
        }
      });

      setSelectedValues(initialFilters);
      setIsInitialized(true);

      // ✅ Call dependent APIs if trafficSource is present
      const trafficSourceParam = initialFilters["trafficsource"];
      if (trafficSourceParam) {
        (async () => {
          dispatch(setLoadingTrafficSourceFilters(true));
          const queryId =
            dataSet === "click"
              ? TrendsDataSetQueryIds.CLICK
              : TrendsDataSetQueryIds.ACTION;
          const params: TrendsQueryParams = {
            oauthTokenHeader,
            queryId,
            startDate: selectedStartDate.format("YYYY-MM-DD"),
            endDate: selectedEndDate.format("YYYY-MM-DD"),
            timeUnit: "day",
            type: fetchBy === "organization" ? "dimension" : fetchBy,
          };
          params.websiteSharedUids = [defaultWebsiteUid];
          params.dimensions = [defaultDimension];
          if (fetchBy === "site") {
            params.websiteSharedUids =
              selectedSharedUid.length > 0
                ? selectedSharedUid
                : [defaultWebsiteUid];
          } else if (fetchBy === "dimension") {
            params.dimensions =
              selectedSharedUid.length > 0
                ? selectedSharedUid
                : [defaultDimension];
          } else if (fetchBy === "organization") {
            params.dimensions =
              organizationDimensions.length > 0
                ? organizationDimensions.map((org: any) => org.uid)
                : [defaultDimension];
          }

          const bizLayerParams = {
            oauthTokenHeader,
            type: fetchBy === "organization" ? "dimension" : fetchBy,
            websiteSharedUids: params.websiteSharedUids || [],
            dimensions: params.dimensions || [],
            trafficSource: trafficSourceParam,
          };
          const campaignParams: CampaignFilterParams = {
            queryId: TrendsFilterQueryIds.CAMP,
            ...bizLayerParams,
            key: "campaign",
            account_shared_uid: 1,
            campaign_status: "all",
          };

          try {
            await Promise.all([
              TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends(
                campaignParams,
              ),
              TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends({
                queryId: TrendsFilterQueryIds.ADGROUP, // adgroup
                ...bizLayerParams,
                key: "adgroup",
              }),
              TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends({
                queryId: TrendsFilterQueryIds.KEYWORD, // keyword
                ...bizLayerParams,
                key: "keyword",
              }),
            ]);
          } catch (error) {
            console.error(
              "Error fetching dependent filter data on mount:",
              error,
            );
          } finally {
            dispatch(setLoadingTrafficSourceFilters(false));
          }
        })();
      }
    }
    // eslint-disable-next-line
  }, [userData?.data]);

  // Generic function to get and process data for any filter
  const getFilterData = (config: FilterConfig) => {
    let rawData;

    if (config.hasQueryOutput && config.dataPath) {
      // For data wrapped in query_output (like campaign, adgroup)
      rawData =
        filterBizLayerQueryDataMap?.[config.dataPath]?.query_output || [];
    } else {
      // For direct data access (like trafficSourceData, geoData)
      if (config.directDataPath === "trafficSourceData") {
        rawData = trafficSourceData || [];
      } else if (config.directDataPath === "geoData") {
        rawData = geoData || [];
      } else {
        rawData = [];
      }
    }

    const validData = rawData.filter(
      (item: any) => item?.[config.idField] && item?.[config.nameField],
    );

    return Array.from(
      new Map(
        validData.map((item: any) => [item[config.idField], item]),
      ).values(),
    ) as any[];
  };

  // Generic function to filter data based on search term
  const getFilteredData = (config: FilterConfig) => {
    const data = getFilterData(config);
    const searchTerm = searchTerms[config.key] || "";
    const selectedValue = selectedValues[config.key];

    let filteredData = data;

    // Apply search filter
    if (searchTerm) {
      filteredData = data.filter((item: any) =>
        item[config.nameField]
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
    }

    // Sort data to put selected option at the top
    if (selectedValue) {
      filteredData = filteredData.sort((a: any, b: any) => {
        const aId = String(a[config.idField]);
        const bId = String(b[config.idField]);
        const selectedId = String(selectedValue);

        // If 'a' is selected, it should come first
        if (aId === selectedId && bId !== selectedId) return -1;
        // If 'b' is selected, it should come first
        if (bId === selectedId && aId !== selectedId) return 1;
        // If both or neither are selected, maintain original order
        return 0;
      });
    }

    return filteredData;
  };
  useEffect(() => {
    if (trendsFilters.length > 0) {
      fetchFilteredTrendsData(selectedSharedUid);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsFilters]);
  const handleClearIndividualFilter = async (filterKey: string) => {
    dispatch(clearAllTimeUnitsData());
    dispatch(clearAllTimeUnits());
    const newParams = new URLSearchParams(searchParams.toString());
    const paramKey = getFilterParamKey(filterKey);
    newParams.delete(paramKey);

    if (filterKey === "trafficsource") {
      dispatch(setLoadingTrafficSourceFilters(true));
      // Clear all other filters when traffic source is cleared
      setSelectedValues({});
      dispatch(clearTrendsFilters());

      // Clear all filter params from URL
      FILTER_CONFIGS.forEach((config) => {
        const paramKey = getFilterParamKey(config.key);
        newParams.delete(paramKey);
      });
      setSearchParams(newParams);

      dispatch(setLoadingTrafficSourceFilters(false));
      // Execute API with empty filters - this won't trigger useEffect since trendsFilters is empty
      fetchFilteredTrendsData(selectedSharedUid, []);
    } else {
      // For other filters, update state and Redux
      // Remove from local state
      setSelectedValues((prev) => {
        const updated = { ...prev };
        delete updated[filterKey];
        return updated;
      });

      // Remove from Redux store - this will trigger useEffect
      dispatch(removeTrendsFilter(filterKey));

      // Update URL params
      setSearchParams(newParams);
    }
  };

  // Generic option change handler
  const handleOptionChange =
    (filterKey: string) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSelectedValues((prev) => ({ ...prev, [filterKey]: value }));
      dispatch(setTrendsFilter({ key: filterKey, value }));
      setOpen(false);
      setClicked(new Array(FILTER_CONFIGS.length).fill(false));
      dispatch(clearAllTimeUnitsData());
      dispatch(clearAllTimeUnits());
      if (filterKey === "trafficsource") {
        dispatch(setLoadingTrafficSourceFilters(true));
        setSelectedValues({ trafficsource: value });
        dispatch(clearTrendsFilters());
        dispatch(setTrendsFilter({ key: filterKey, value }));
        const defaultWebsiteUid = Object.keys(
          userData?.data?.website_permissions[0],
        )[0];
        const defaultDimension = userData?.data?.dimensions?.[0];
        const queryId =
          dataSet === "click"
            ? TrendsDataSetQueryIds.CLICK
            : TrendsDataSetQueryIds.ACTION;
        const params: TrendsQueryParams = {
          oauthTokenHeader,
          queryId,
          startDate: selectedStartDate.format("YYYY-MM-DD"),
          endDate: selectedEndDate.format("YYYY-MM-DD"),
          timeUnit: "day", // This seems to need fixing in original code
          type: fetchBy === "organization" ? "dimension" : fetchBy,
        };

        params.websiteSharedUids = [defaultWebsiteUid];
        params.dimensions = [defaultDimension];

        if (fetchBy === "site") {
          params.websiteSharedUids =
            selectedSharedUid.length > 0
              ? selectedSharedUid
              : [defaultWebsiteUid];
        } else if (fetchBy === "dimension") {
          params.dimensions =
            selectedSharedUid.length > 0
              ? selectedSharedUid
              : [defaultDimension];
        } else if (fetchBy === "organization") {
          params.dimensions =
            organizationDimensions.length > 0
              ? organizationDimensions?.map((org: any) => org.uid)
              : [defaultDimension];
        }
        const bizLayerParams = {
          oauthTokenHeader,
          type: fetchBy === "organization" ? "dimension" : fetchBy,
          websiteSharedUids: params.websiteSharedUids || [],
          dimensions: params.dimensions || [],
          trafficSource: value,
        };
        const campaignParams: CampaignFilterParams = {
          queryId: TrendsFilterQueryIds.CAMP,
          ...bizLayerParams,
          key: "campaign",
          account_shared_uid: 1,
          campaign_status: "all",
        };

        try {
          await Promise.all([
            TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends(
              campaignParams,
            ),
            TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends({
              queryId: TrendsFilterQueryIds.ADGROUP, // adgroup
              ...bizLayerParams,
              key: "adgroup",
            }),
            TrendsDashboardEndPointLogic.getFilterBizLayerQueryTrends({
              queryId: TrendsFilterQueryIds.KEYWORD, // keyword
              ...bizLayerParams,
              key: "keyword",
            }),
          ]);
        } catch (error) {
          console.error("Error fetching dependent filter data:", error);
        } finally {
          dispatch(setLoadingTrafficSourceFilters(false));
        }
      }
    };

  // Generic search term change handler
  const handleSearchTermChange =
    (filterKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerms((prev) => ({ ...prev, [filterKey]: event.target.value }));
    };

  // Dynamic JSX generator for filter content
  const generateFilterContent = (config: FilterConfig) => {
    const filteredData = getFilteredData(config);
    const selectedValue = selectedValues[config.key] || "";
    const searchTerm = searchTerms[config.key] || "";

    return (
      <div
        className="tabDiv"
        style={{
          width: "300px",
          minHeight: 150,
          maxHeight: 330,
          overflowY: "auto",
          padding: "0px 16px 0px 16px",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#fff",
            marginTop: "-12px",
          }}
        >
          <Box mb={1}>
            <TextField
              placeholder={config.searchPlaceholder}
              size="small"
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchTermChange(config.key)}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.85rem",
                  padding: "8.5px 12px",
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: 14, fontWeight: 500, mb: 2, mt: 2 }}
            >
              {config.displayName}
            </Typography>
          </Box>
        </div>

        {filteredData.length > 0 ? (
          <RadioGroup
            value={selectedValue}
            onChange={handleOptionChange(config.key)}
          >
            <FixedSizeList
              height={250}
              width="100%"
              itemSize={54}
              itemCount={filteredData.length}
              itemData={{
                items: filteredData,
                selectedValue,
                config,
              }}
            >
              {({ index, style, data }) => {
                const item = data.items[index];
                return (
                  <div style={style} key={item[data.config.idField]}>
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.90rem",
                        },
                      }}
                      value={String(item[data.config.idField])}
                      control={<Radio color="secondary" />}
                      label={item[data.config.nameField]}
                    />
                  </div>
                );
              }}
            </FixedSizeList>
          </RadioGroup>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.80rem", color: "gray", pb: 2 }}
          >
            No matching {config.label.toLowerCase()}s found, there may be a
            problem fetching data, or no filter options are available for this
            filter. Please refresh the page or try again later.
          </Typography>
        )}
      </div>
    );
  };

  const handleClearFilter = () => {
    dispatch(clearAllTimeUnitsData());
    dispatch(clearAllTimeUnits());
    setSelectedValues({});
    dispatch(clearTrendsFilters());
    fetchFilteredTrendsData(selectedSharedUid, []);
    const newParams = new URLSearchParams(searchParams.toString());

    FILTER_CONFIGS.forEach((config) => {
      const paramKey = getFilterParamKey(config.key);
      newParams.delete(paramKey);
    });

    setSearchParams(newParams);
  };

  const handleClick =
    (newPlacement: PopperPlacementType, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const config = FILTER_CONFIGS[index];

      // Prevent opening disabled filters
      if (isFilterDisabled(config)) {
        return;
      }
      if (anchorEl === event.currentTarget && open) {
        setOpen(false);
        setClicked(new Array(FILTER_CONFIGS.length).fill(false));
      } else {
        setAnchorEl(event.currentTarget);
        setOpen(true);
        setPlacement(newPlacement);
        setSelectedContentIndex(index);
        setClicked((prevClicked) => prevClicked.map((_, i) => i === index));
      }
    };

  const popperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !anchorEl?.contains(event.target as Node)
      ) {
        setOpen(false);
        setClicked(new Array(FILTER_CONFIGS.length).fill(false));
        setSearchTerms({});
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl]);

  // Count selected filters
  const selectedCount = Object.keys(selectedValues).filter(
    (key) => selectedValues[key],
  ).length;

  return (
    <Box sx={{ width: "100%", paddingLeft: "16px", paddingTop: "8px" }}>
      <Popper
        sx={{ zIndex: 1200 }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper ref={popperRef}>
              {generateFilterContent(FILTER_CONFIGS[selectedContentIndex])}
            </Paper>
          </Fade>
        )}
      </Popper>

      {FILTER_CONFIGS.map((config, index) => {
        const isSelected = !!selectedValues[config.key];
        const isDisabled = isFilterDisabled(config);
        return (
          <Box
            key={config.key}
            sx={{ display: "inline-flex", alignItems: "center", mr: 1 }}
          >
            <Button
              sx={{
                backgroundColor: isSelected
                  ? "rgba(156, 39, 176, 0.08)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: isDisabled
                    ? "transparent"
                    : "rgba(156, 39, 176, 0.04)",
                },
                textTransform: "capitalize",
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
              }}
              key={config.key}
              onClick={handleClick("bottom-start", index)}
              disabled={isDisabled}
              data-tracker={
                config.key === "trafficsource"
                  ? "480"
                  : config.key === "country"
                    ? "481"
                    : config.key === "campaignId"
                      ? "482"
                      : config.key === "adgroupId"
                        ? "483"
                        : config.key === "kwId"
                          ? "484"
                          : null // default or fallback tracker
              }
            >
              <span
                style={
                  isSelected
                    ? { color: "#9C27B0" }
                    : { color: clicked[index] ? "#9C2780" : "#000000DE" }
                }
              >
                {config.label}
              </span>
              {isSelected ? (
                <span style={{ color: "#9C27B0" }}>+1</span>
              ) : (
                <KeyboardArrowDownIcon
                  style={{
                    color: clicked[index] ? "#9C2780" : "#000000DE",
                  }}
                />
              )}
            </Button>
            {isSelected && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent opening the filter dropdown
                  handleClearIndividualFilter(config.key);
                }}
                sx={{
                  ml: 0.5,
                  color: "#9C27B0",
                  "&:hover": {
                    backgroundColor: "rgba(156, 39, 176, 0.08)",
                  },
                  width: 24,
                  height: 24,
                }}
                title={`Clear ${config.label} filter`}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Button
        sx={{
          textTransform: "capitalize",
          fontWeight: 500,
          color: "#000000DE",
          "&:hover": {
            color: "#9C27B0",
            backgroundColor: "rgba(156, 39, 176, 0.04)",
          },
        }}
        onClick={handleClearFilter}
        disabled={selectedCount === 0}
        data-tracker="485"
      >
        Clear Filter {selectedCount > 0 && `(${selectedCount})`}
      </Button>
      {!isTrafficSourceSelected && (
        <Box
          sx={{
            mt: 1.5,
            ml: 1,
            display: "flex",
            alignItems: "center",
            fontSize: "0.8rem",
            color: "#6b6b6b",
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: "1rem",
              mr: 0.5,
              color: "#f57c00", // optional: use warning color
            }}
          />
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#6b6b6b",
              background:
                "linear-gradient(90deg, #5a5a5aff 25%, #bebdbdff 50%, #5a5a5aff 75%)",
              backgroundSize: "300% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "shimmer 8s linear infinite",
            }}
          >
            Please select a Traffic Source first — it’s required before applying
            any other filters.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
